import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
import Api from '../utils/Api';
// v0.18.39 — 教练 / 学员改用可搜索单选 (与普通约课 modal 同一个组件)。
//   组件本身的交互细节 (浮动定位 / 向上翻转 / 候选项选中态) 已经过 v0.16.6~v0.17.25
//   多轮 V 测, 抄第二份 = 下一轮回归要测两遍。
import SearchableUserPicker from '../utils/SearchableUserPicker';
// v0.18.39 — 「没配课表 / 问失败 / 加载中」三态占位, 与普通约课 modal **同一份文案与出口**
//   (见 utils/ScheduleMissingNotice.js 顶部注释: 那三句必须是全仓库唯一的一份)。
import { SCHEDULE_LOADING_TEXT, scheduleMissingCompact, scheduleErrorCompact } from '../utils/ScheduleMissingNotice';
// v0.19.20 (批 41 · 段②) — 「分区骨架」改用**共用入口**
//   (本仓铁律「一个逻辑一个入口, 不许抄两遍」; 改前这个原语在本文件里抄了一份)。
//   ⚠️ 共用入口在 `utils/FormPrimitives.js` —— 本文件的 DOM 结构与样式**一字不动**。
import { renderSection, errReason } from '../utils/FormPrimitives';

// v0.18.77 (D3) — 周几中文标签 (**按 getDay() 索引**: 0=周日 .. 6=周六)。
//   ⚠️ 生产 CalendarTab.fixedDowOptions() 的**数组顺序**是「一二三四五六日」(周日排最后),
//      ⛔ **不许**拿它的数组位置当索引用 (单个周几显示时两者结果一致, 但语义不同)。
//      本批只用 `DOW_LABEL[getDay()]` 这一种取法。
const DOW_LABEL = {
  0: '日',
  1: '一',
  2: '二',
  3: '三',
  4: '四',
  5: '五',
  6: '六'
};

/**
 * FixedLessonModal — 「添加固定课」弹窗 (v0.9.0 起, v0.18.39 重构)
 *
 * ## v0.18.39 为什么从「纯 DOM 浮层」改成 mithril Modal 子类
 *   改前 (js/src/forum/utils/FixedLessonModal.js) 是一个 `document.createElement` 拼出来的
 *   浮层 (ZSFX 对象 + 自绘 _root / _detachFns / close()), 理由写在那个文件的头注释里:
 *   Flarum 2.0 的 `m` 是 **Preact 不是 Mithril**, `m.render()` 直接不可用。
 *   但辉哥 10/21 提的四条要求 (时间=时间段下拉 / 学员教练=搜索框 / 教室卡=原生 Dropdown /
 *   排版与普通约课 modal 一致) **全都要求组件树**:
 *     · Flarum 原生 `Dropdown` 是 mithril 组件, 塞不进纯 DOM 容器;
 *     · `SearchableUserPicker` 更是 mithril 组件 (它的 render(key) 返回 JSX)。
 *   ⇒ 必须走 `app.modal.show(FixedLessonModal, {...})`。
 *   ⚠️ `FixedCheckModal.js` (另一个「检查排课」浮层) **不在本轮范围**, 它仍是纯 DOM。
 *
 * ## 参照实现 (逐条照抄, 不自己发明)
 *   · 时间段下拉   ← LessonEditorModal.timeSlotDropdown()   (L1459)
 *   · 教室下拉     ← LessonEditorModal.roomDropdown()        (L1527)
 *   · 学员卡下拉   ← LessonEditorModal.studentCardDropdown()  (L1577)
 *   · 教练/学员框  ← LessonEditorModal.normalPicker           (L356)
 *   · 分区外壳     ← LessonEditorModal.renderSection()        (L1818)
 *   · 底部按钮     ← LessonEditorModal.content() 的 Modal-footer (L2512)
 *
 * ## 固定课与普通约课的**两处业务差异** (刻意保留, 不是漏抄)
 *   ① 结束时间 = 所选**时间段节点的末尾** (普通约课 = 开始时间 + 卡模板 minutes)。
 *      固定课是「模板」, 时长跟时间段走 —— 改前 CalendarTab 预填的 endTime 就是
 *      节点 end_time (见 CalendarTab.openAddFixedLesson L3541-3544), 口径一致。
 *   ② 星期 (state.days) **不在本弹窗里选** (v0.17.3 起), 只由调用方给, 本文件原样透传。
 *
 * ## attrs
 *   - schoolId:    number   校区 id
 *   - rooms:       Array    教室列表 (调用方 CalendarTab 已经加载好, 不重复请求)
 *   - roomId:      number   预填教室 (右键所在列)
 *   - dateStr:     string   'YYYY-MM-DD' (用于卡的有效期判断 + 课表兜底请求的日期)
 *   - startTime:   string   'HH:MM' 预填 (右键格子, 已经是某个时间段的 start_time)
 *   - endTime:     string   'HH:MM' 预填
 *   - dayOfWeek:   number   0=周日..6=周六
 *   - selectedDays:Array    多选星期 (预填 state.days)
 *   - scheduleNodes: Array  **可选** 调用方已拿到的课表节点 (见下面「时间段的数据源」)
 *   - scheduleNodeId:number **可选** 预填的课表节点 id (调用方已经知道用户点的是哪一行)
 *   - onDone:      Function 提交成功回调
 */
export default class FixedLessonModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    const a = vnode.attrs || {};
    this.schoolId = a.schoolId || null;
    this.dateStr = a.dateStr || '';
    this.rooms = a.rooms || [];

    // ⚠️ v0.17.3 起「每周哪几天」的多选 UI 整块删掉了, 星期**只由调用方**决定。
    //   这里只做搬运, 不做任何增删 —— 提交时的 Array.from(this.days) 语义与改前逐字相同。
    this.days = new Set(a.selectedDays && a.selectedDays.length ? a.selectedDays : [a.dayOfWeek]);

    // 预填时间 (右键格子)。它们**已经是**某个课表节点的 start/end
    //   (CalendarTab.openAddFixedLesson 用 realTimeForSlotTime / scheduleNodeForSlotTime 换算过),
    //   所以时间段下拉能直接命中这一档。
    this.startTime = a.startTime || '';
    this.endTime = a.endTime || '';

    // v0.18.40 — 「用户选了哪一档」(课表节点 id)。
    //   调用方 (日历右键建固定课) 已经知道用户点的是**哪一行**, 直接把那个节点 id 递进来。
    //   它比「按 startTime 反查」更准: 反查用的是「包含该时刻的第一段」
    //   (ScheduleResolver::nodeIn / scheduleIndexOf 的口径), 课表允许区间重叠时
    //   同一时刻可能落在好几段里 ⇒ 反查会认成**前一档**。
    //   写法照抄 LessonEditorModal.oninit 的 prefillNodeId (那边是同一个意思)。
    //   ⚠️ 拿不到就留 null ⇒ 后面走 startTime 反查的兼容路径, 行为与改前一致。
    this.scheduleNodeId = a.scheduleNodeId != null && Number(a.scheduleNodeId) > 0 ? Number(a.scheduleNodeId) : null;
    this.notes = '';
    this.errorMsg = '';
    this.saving = false;

    // ── v0.18.77 (D3) — 排期预测 (只读) ───────────────────────────────
    //   ⚠️⚠️ **实例字段名不许叫 `view`** —— Mithril/Preact 的 Component 基类
    //   **原型上就有 `view()` 方法** ⇒ 写成 `this.view = {...}` 会遮蔽它 ⇒
    //   渲染时内部调 `this.view(...)` ⇒ 抛 `TypeError: this.apply is not a function`
    //   ⇒ **整个弹窗渲染不出来**。
    //   ⇒ 预测数据一律放 `this.forecast` (⛔ 不是 this.view / this.render / this.data…)。
    this.forecast = null;
    //   参数签名: 用来判断「要不要重拉」 (⛔ 不许每次 redraw 都发请求)。
    this.forecastSignature = null;
    this.room = null;
    this.coach = null;
    this.student = null;
    this.studentCard = null;
    this.studentCards = [];
    this.students = [];

    // ── 课表 (时间段下拉的数据源) ─────────────────────────────────────
    //   三态: scheduleResolved (问出结论了没有) / scheduleFailed (问失败了) / scheduleNodes (有课表)
    this.scheduleNodes = null;
    this.scheduleResolved = false;
    this.scheduleFailed = false;
    //   调用方若把课表节点一并递进来, 就用它 —— 见 loadSchedule() 的「为什么优先用它」。
    this.injectedScheduleNodes = Array.isArray(a.scheduleNodes) ? a.scheduleNodes : null;

    // 预填教室 (右键所在列); 找不到就退回第一个 (与改前 `_selectField` 的默认值口径一致)
    if (a.roomId != null && a.roomId !== '') {
      this.room = this.rooms.find(r => String(r.id) === String(a.roomId)) || null;
    }
    if (!this.room && this.rooms.length) this.room = this.rooms[0];

    // ── 教练 / 学员 可搜索单选 (与普通约课 modal 同一个组件) ──────────────
    this.picker = new SearchableUserPicker({
      host: this,
      getSchoolId: () => this.schoolId,
      getValue: key => {
        if (key === 'fx_coach_id') {
          return this.coach ? String(this.coach.id || this.coach.user_id) : '';
        }
        return this.student ? String(this.student.user_id || this.student.id) : '';
      },
      setValue: (key, v, item) => {
        if (key === 'fx_coach_id') {
          this.coach = item || null;
          m.redraw();
          return;
        }
        // ⚠️ 必须走 onStudentChange (不能直接赋值) —— 学员卡联动整条链都在那里。
        this.onStudentChange(item || null);
      },
      // 回显用: 候选里找不到当前值时用它显示名字 (本弹窗是纯新建, 实际用不到, 但接口对齐)
      currentObject: key => key === 'fx_coach_id' ? this.coach || null : this.student || null,
      // 与普通约课 modal 同口径: 老下拉是「平铺全部候选不截断」, 保持一致
      maxVisible: 200,
      // ⚠️ 必须开: ziven-core v0.5.8 全局 override 了 Modal.prototype.onready,
      //   强制聚焦 modal 里的**第一个** input。教练框换了 <input type="text"> 之后它就成了
      //   第一个 input ⇒ 弹窗一开搜索框就自己弹开了。开关默认 false, 只有本实例显式开。
      //   ⚠️ 绝不能在这个 Modal 里定义 onready() —— 子类方法会遮蔽 Modal.prototype 上
      //   那条 patch (ziven-core 的 iOS 14+ 修复), 正好把它废掉。
      suppressAutofocusOpen: true,
      fields: {
        fx_coach_id: {
          // role 分支: 组件自己拉 GET /users?role=coach&school_id=N
          //   ⇒ 与改前固定课 modal 的那个请求**逐字相同** (Api.list('users', {role:'coach', school_id})).
          role: 'coach',
          bare: true,
          placeholder: '输入教练姓名筛选…',
          emptyText: '当前校区下没有教练'
        },
        fx_student_id: {
          // ⚠️ 学员**不能**改走 /users?role=student (见 LessonEditorModal L348-352 的实测):
          //   students-list 8 人 vs /users?role=student 7 人, 差的是不在「学员」组里的人。
          //   所以沿用普通约课 modal 的做法: 宿主自己拉 students-list, 组件用 pick 复用。
          pick: () => this.students,
          idOf: s => s.user_id || s.id,
          bare: true,
          placeholder: '输入学员姓名筛选…',
          emptyText: '当前校区下没有学员'
        }
      }
    });
    this.load();

    // v0.18.77 (D3) — 首次就拉一次排期预测。
    //   ⛔ 这条信息只跟「星期几 + 校区」有关, **跟学员无关** ⇒ 换学员**不许**重拉
    //      (设计稿验收第 2 条)。换教室 / 换时间段才要重拉 (判冲突要最新的槽位)。
    //   ⚠️ 首次就能把 room_id / schedule_node_id / start_time **全部带上** (弹层一打开
    //      就有教室, 时间段也已预填) ⇒ 少一半请求。
    this.loadForecast();
  }
  className() {
    // ⚠️ 作用域锁在 .ZS-FixedLessonModal: 固定课自己的 LESS (ZS-fx-*) 只挂在这条下面,
    //   绝不影响普通约课 / 体验课那两个 modal。
    return 'Modal--medium ZS-BookingModal ZS-FixedLessonModal';
  }
  title() {
    return '添加固定课';
  }

  // ─── 生命周期 ────────────────────────────────────────────────────────
  //   mount / afterUpdate / destroy 必须成对, 否则 modal 关掉后 document 监听还活着,
  //   每次全站 mousedown 都会跑进一个已死实例 (与 LessonEditorModal 同一个坑)。
  oncreate(vnode) {
    super.oncreate(vnode);
    this.picker.mount();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    // 候选列表是这轮 patch 才插进 DOM 的, 需要重新判定要不要向上翻
    this.picker.afterUpdate();
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.picker.destroy();
  }

  // ─── 加载 ───────────────────────────────────────────────────────────
  async load() {
    await Promise.all([this.loadSchedule(), this.loadStudents()]);
    // 教练候选由 picker 自己的 role 分支拉 (loadCandidates); 学员候选在上面已就位,
    // 这里统一 loadAll() 把两个 key 的候选都灌好 (pick 分支同步, role 分支发请求)。
    try {
      await this.picker.loadAll();
    } catch (e) {
      console.error('[fixed-lesson-modal] load candidates failed', e);
    }
    // 候选拉完后重绘一次 —— picker 的候选存在它自己的 state 里, 不重绘用户看不到。
    m.redraw();
  }

  /**
   * 拉该学员名下所有卡 (含 template)。
   * ⚠️ 选完学员要联动拉卡 —— 决策 B6「固定课必须绑学员卡」, 学员一选中就得有卡可选。
   *
   * v0.18.40 — **必须带 school_id** (与 LessonEditorModal.studentCardDropdown 同款)。
   *   改前只传 student_id ⇒ 后端 ListStudentCardsController 收不到 school_id 就退到
   *   visibleSchoolIds ⇒ 把**同一学员在别的可见校区的卡**也列出来。
   *   实测: 学员「沈亦然」(user id 266) 在 school 1005 有一张卡 ⇒ 固定课 modal 的卡下拉里
   *   出现了「10次卡 · 剩 9」(那是 1005 的) ⇒ 选中提交 ⇒ 后端 422「所选学员卡不属于该校区」
   *   ⇒ 用户白填一遍表单。带 school_id 之后那张卡根本不会出现在下拉里。
   */
  async loadStudentCards(student) {
    try {
      const cards = await Api.list('student-cards', {
        student_id: student.user_id || student.id,
        ...(this.schoolId ? {
          school_id: this.schoolId
        } : {})
      });
      this.studentCards = cards || [];
    } catch (e) {
      this.studentCards = [];
    }
    // 默认选第一张**接口判定为可用**的卡; 一张都不可用就选第一张 (提示文案在 dropdown label 上, 不静默禁用)
    // v0.18.41 — 改前是 `find((c) => this.cardWhy(c) === '') || this.studentCards[0]`:
    //   「无提示」这个判据**只能**说明前端自己没看出问题, 说明不了服务端认为它可用
    //   (口径见 cardWhy 的注释) ⇒ 现在**优先**选接口判定为可用的那张。
    if (!this.studentCards.find(c => String(c.id) === String(this.studentCard && this.studentCard.id))) {
      const usable = this.studentCards.find(c => c.is_available === true && c.status === 'active');
      const first = usable || this.studentCards.find(c => this.cardWhy(c) === '') || this.studentCards[0];
      this.studentCard = first || null;
    }
    m.redraw();
  }

  /** 学员候选 (students-list) —— 与普通约课 modal 的 this.students 同一个来源与同一个请求。 */
  async loadStudents() {
    try {
      const res = await Api.get('students-list', {
        ...(this.schoolId ? {
          school_id: this.schoolId
        } : {}),
        limit: 200
      });
      this.students = res && res.students || [];
    } catch (e) {
      this.students = [];
      // v0.18.41 — 加载失败**不再静默吞掉**。
      //   改前是 `catch (e) { this.students = []; }` ⇒ 「请求失败」与「这个校区真的没学员」
      //   在界面上长得一模一样 (下拉框空着) ⇒ 用户以为自己没得选, 实际是网络/接口出问题。
      //   旧 DOM 版 (改前 v0.17.x 的浮层) 的做法是给用户看一句提示, 这里照抄那句文案。
      //   ⚠️ 用的是本 modal **既有**的提示位 (this.errorMsg, 见 content() 里的渲染块) ——
      //      不自创一套错误展示机制。
      //   ⚠️ loadSchedule() 的失败**另有**三态处理 (scheduleFailed → 重试面板), 本轮不动。
      this.errorMsg = '加载教练/学员失败, 请重试';
    }
  }

  /**
   * 读校区课表 (时间段清单)。
   *
   * ## 为什么**优先**用调用方递进来的节点 (而不是自己再拉一次)
   *   调用方 (CalendarTab) 拿节点时**带了日期** (Api.getCurrentSchedule(schoolId, {date})),
   *   拿到的正是「日历上这一天生效的那一版课表」。本弹窗的时间段选项必须跟用户**眼前
   *   那张网格**是同一版 —— 否则会出现 v0.17.85 那一类分叉 (看 10-16 拿到 B 版的时段,
   *   而 10-16 的课挂着 A 版的节点)。
   *   改成自己拉还要多一次往返, 而且得记得带上同一个 date 才不出分叉。
   *   ⇒ 首选「调用方递进来」(零请求、零分叉); 调用方没递时才自己兜底拉一次。
   */
  async loadSchedule() {
    this.scheduleResolved = false;
    this.scheduleFailed = false;
    if (this.injectedScheduleNodes) {
      // 调用方给了答案 (可能是空数组 = 那天真没配课表) ⇒ 不用再问
      this.scheduleNodes = this.injectedScheduleNodes.length ? this.injectedScheduleNodes : null;
      this.scheduleResolved = true;
      return;
    }
    try {
      const res = await Api.getCurrentSchedule(this.schoolId, this.dateStr ? {
        date: this.dateStr
      } : undefined);
      if (res && res.reason === null && res.data && Array.isArray(res.data.nodes) && res.data.nodes.length) {
        // 与 CalendarTab / LessonEditorModal 同口径: sort_order 升序, 同分用 id 兜底
        const nodes = res.data.nodes.slice().sort((a, b) => Number(a.sort_order) - Number(b.sort_order) || Number(a.id) - Number(b.id));
        this.scheduleNodes = nodes;
      } else {
        this.scheduleNodes = null;
      }
      this.scheduleResolved = true;
    } catch (e) {
      // ⚠️ 「问失败」不是「没配」—— 两者必须分开 (见 ScheduleMissingNotice.js 的注释)
      this.scheduleNodes = null;
      this.scheduleFailed = true;
      this.scheduleResolved = true;
    }
  }

  // ─── 联动 ───────────────────────────────────────────────────────────
  onStudentChange(student) {
    this.student = student || null;
    this.studentCard = null;
    if (student) {
      this.loadStudentCards(student);
    } else {
      this.studentCards = [];
    }
    m.redraw();
  }

  // ─── v0.18.77 (D3) ─ 排期预测 (只读) ─────────────────────────────────
  /**
   * 当前生效的星期几 (0=周日 .. 6=周六)
   *
   * ⚠️ 取 `Array.from(this.days)[0]` —— **不是** `a.dayOfWeek`:
   *   days 是 Set, 来源是 `a.selectedDays` (优先) 兜底 `[a.dayOfWeek]`。
   */
  currentDow() {
    const list = Array.from(this.days || []);
    const raw = list.length ? list[0] : null;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 && n <= 6 ? n : null;
  }

  /**
   * 「至 MM-DD」—— 跨年时显示**完整** YYYY-MM-DD
   * (设计稿原文: 「跨年时显示至 2027-01-06」⇒ 就是这个意思。
   *  `last_date.slice(5)` 在跨年时会给出 `01-06` (丢年份) ⇒ 必须判年。)
   */
  lastLabel(dateStr) {
    if (!dateStr) return '';
    const s = String(dateStr);
    const thisYear = String(new Date().getFullYear());
    return s.slice(0, 4) === thisYear ? s.slice(5) : s;
  }

  /**
   * 拉排期预测 (只读 GET /ziiven/scheduler/fixed-lessons/forecast)
   *
   * ⚠️ **参数签名**去重: 签名不变就**不重拉** (⛔ 不许每次 redraw 都发请求)。
   *   签名 = school_id | dow | room_id | schedule_node_id | start_time
   * ⚠️ 换**学员**不重拉 (信息只跟星期+校区有关); 换**教室 / 时间段**要重拉。
   * ⚠️ 「共 N 次」由**后端**下发 (`forecast.count`) —— ⛔ 前端不许写死 5
   *   (窗口起点 = 今天 ⇒ 过了今天就少一次)。
   */
  async loadForecast() {
    const schoolId = this.schoolId;
    const dow = this.currentDow();
    const roomId = this.room ? this.room.id : null;
    const nodeId = this.scheduleNodeId != null ? +this.scheduleNodeId : null;
    const startTime = this.startTime || null;
    if (!schoolId || dow === null) {
      this.forecast = null;
      this.forecastSignature = null;
      return;
    }
    const signature = [schoolId, dow, roomId || '', nodeId || '', startTime || ''].join('|');
    if (signature === this.forecastSignature) return; // 签名没变 ⇒ 不重拉
    this.forecastSignature = signature;
    try {
      const res = await Api.forecastFixedLessons(schoolId, dow, {
        room_id: roomId,
        schedule_node_id: nodeId,
        start_time: startTime
      });
      // ⚠️ 竞态: 响应回来时签名可能又变了 ⇒ 丢掉这次过期响应, 别拿旧数据盖新的
      if (this.forecastSignature !== signature) return;
      this.forecast = res || null;
    } catch (e) {
      if (this.forecastSignature !== signature) return;
      this.forecast = null;
      // 失败时清签名 ⇒ 下次**显式**触发还能重试 (⛔ 不会变成「每次 redraw 都发」)
      this.forecastSignature = null;
    }
    m.redraw();
  }

  // ─── 时间段 ─────────────────────────────────────────────────────────
  /**
   * 课表里的时间段清单 → 下拉选项。与 LessonEditorModal.computeTimeSlots() 同口径。
   * ⚠️ shape `{ nodeId, startTime, endTime, label }` 四个字段都不可省:
   *   nodeId 是「用户选了哪一档」的**载体** —— 课表允许**区间重叠**, 同一个**时刻**可能同时
   *   落在好几段里 (后端 ScheduleResolver::nodeIn 的口径是「按 sort_order 取第一条命中的」),
   *   所以「这一档」不能靠时刻反推, 得靠 id。
   *   v0.18.40 起它不再只是下拉里的一个字段 —— 选完之后一路带到提交载荷的
   *   `schedule_node_id`, 后端落库 ⇒ 物化出的课次也带着它 ⇒ 日历画在用户选的那一行。
   */
  computeTimeSlots() {
    if (!Array.isArray(this.scheduleNodes) || this.scheduleNodes.length === 0) return [];
    return this.scheduleNodes.map(n => {
      const startTime = String(n.start_time).slice(0, 5);
      const endTime = String(n.end_time).slice(0, 5);
      return {
        nodeId: n.id != null ? +n.id : null,
        startTime: startTime,
        endTime: endTime,
        label: `${startTime} – ${endTime}`
      };
    });
  }

  /**
   * 当前选中的时间段 (**不看 endTime**)。
   *
   * v0.18.40 — **优先按 scheduleNodeId 匹配**, 没有才退回按 startTime 匹配
   * (与 LessonEditorModal.slotByStartTime() 逐字同口径)。
   * 为什么要优先按 id: 课表允许**区间重叠**, 同一个**时刻**可能同时落在好几段里。
   * 本方法的 startTime 回退分支是按 **start_time 全等** 匹配的 (start 严格递增 ⇒ 全表唯一),
   * 所以它本身不会认错档; 优先按 id 是为了跟后端「用户选了哪一档」的口径**完全一致**,
   * 少一处可能分叉的地方 (后端那侧是「按时刻落在哪一段」判的, 与这里是两套口径)。
   * ⚠️ 匹配不到 → null (如实显示「请选择时间段」, **不静默改用户的数据**)。
   */
  slotByStartTime(startTime) {
    const slots = this.computeTimeSlots();
    const wantedId = this.scheduleNodeId != null ? +this.scheduleNodeId : null;
    if (wantedId != null) {
      const byId = slots.find(s => s.nodeId != null && +s.nodeId === wantedId);
      if (byId) return byId;
    }
    if (!startTime) return null;
    return slots.find(s => s.startTime === startTime) || null;
  }

  /**
   * 选时间段 → 带出 start_time / end_time, 并**记下这一档的节点 id**。
   * ⚠️ 固定课的时长跟**时间段**走 (模板语义), 不是「开始 + 卡模板 minutes」
   *   —— 那是普通约课 modal 的口径 (LessonEditorModal.recalcEndFromCard), 两者刻意不同。
   * v0.18.40 — 同时记 scheduleNodeId (与 LessonEditorModal.onTimeSlotSelect 同款):
   *   「用户选的就是它」, 不能靠 startTime 反推 —— 反推会把重叠区里的选择认成前一档。
   */
  onTimeSlotSelect(slot) {
    this.startTime = slot.startTime;
    this.endTime = slot.endTime;
    this.scheduleNodeId = slot.nodeId != null ? +slot.nodeId : null;
    m.redraw();
    // v0.18.77 (D3) — 换了时间段 ⇒ 判冲突要用最新的槽位 ⇒ 重拉一次预测。
    this.loadForecast();
  }

  /** 时间段 dropdown —— 逐字照抄 LessonEditorModal.timeSlotDropdown() 的结构与三态口径 */
  timeSlotDropdown() {
    const slots = this.computeTimeSlots();
    if (slots.length === 0) {
      // 三态分流: 还在加载 / 问失败了 / 确认没配
      if (!this.scheduleResolved) {
        return m("div", {
          className: "ZS-form-input"
        }, SCHEDULE_LOADING_TEXT);
      }
      if (this.scheduleFailed) {
        return scheduleErrorCompact(() => this.loadSchedule());
      }
      return scheduleMissingCompact(() => this.hide());
    }
    const current = this.slotByStartTime(this.startTime);
    const label = current ? current.label : '请选择时间段';

    // ⚠️ 所有兄弟 element 必须合并到 slots.map 的 callback 里, 共享 vnode.key 模式
    //   (写成两个子节点会让 Flarum Dropdown 只包出一个 <li>, core CSS 直接子选择器全废)
    const items = [];
    slots.forEach(s => {
      items.push(m(Button, {
        key: s.nodeId != null ? `n${s.nodeId}` : `${s.startTime}-${s.endTime}`,
        active: !!current && (current.nodeId != null && s.nodeId != null && current.nodeId === s.nodeId || current.startTime === s.startTime),
        onclick: () => this.onTimeSlotSelect(s)
      }, s.label));
    });
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      menuClassName: "ZS-followup-dropdown-menu ZS-tf-menu",
      label: label
    }, items);
  }

  // ─── 下拉 ───────────────────────────────────────────────────────────
  /**
   * 选教室 —— v0.18.77 从 roomDropdown() 的内联 onclick 里**抽出来**
   *
   * ⚠️ 语义**逐字不变** (仍然是「选中 + 重绘」), 只是多一次 forecast 重拉
   *   (判冲突要最新的教室)。抽成方法是**必要**的: 冲突判定依赖教室,
   *   而内联 onclick 里没法挂「重拉」这一串。
   */
  pickRoom(r) {
    this.room = r;
    m.redraw();
    this.loadForecast();
  }

  /** 教室 dropdown (照 LessonEditorModal.roomDropdown()) */
  roomDropdown() {
    const current = this.room;
    const label = current ? current.name : '请选择教室';
    if (!this.rooms.length) {
      return m("div", {
        className: "ZS-form-input"
      }, label);
    }
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      label: label
    }, this.rooms.map(r => m(Button, {
      key: r.id,
      active: !!current && current.id === r.id,
      onclick: () => this.pickRoom(r)
    }, r.name)));
  }

  /**
   * 学员卡 dropdown (照 LessonEditorModal.studentCardDropdown())。
   *
   * ⚠️ **保留**改前那套「卡已停用 / 未激活 / 已过期」的提示 (决策 B6):
   *   「卡过期也允许建固定课」是合法业务 (卡会续费, 模板会从 paused_card_expired 自动恢复),
   *   所以只是**标灰 + 注明原因**, 绝不静默禁用 —— 静默禁用等于让用户以为系统坏了。
   */
  studentCardDropdown() {
    if (!this.student) {
      return m("div", {
        className: "ZS-form-input"
      }, "\u8BF7\u5148\u9009\u62E9\u5B66\u5458");
    }
    if (!this.studentCards.length) {
      return m("div", {
        className: "ZS-form-input"
      }, "\u8BE5\u5B66\u5458\u6682\u65E0\u5B66\u5458\u5361");
    }
    const current = this.studentCard;
    const label = current ? this.cardLabel(current) : '请选择学员卡';
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      label: label
    }, this.studentCards.map(c => m(Button, {
      key: c.id,
      active: !!current && String(current.id) === String(c.id),
      onclick: () => {
        this.studentCard = c;
        m.redraw();
      }
    }, this.cardLabel(c))));
  }

  /**
   * 学员卡不可用的原因 (改前 cardOpts 的那套文案, **一个字没改**)。
   *
   * ## v0.18.41 — 「是否过期」这一项改用接口给的 `is_available` (服务端权威口径)
   *   改前是 `card.expires_at && card.expires_at < this.dateStr` —— 拿 **expires_at**
   *   跟**上课日** (this.dateStr) 比; 而服务端 `StudentCard::isAvailable()` 对的是
   *   **现在** (到期日 00:00 即过期) ⇒ 两边口径不同, 会出现「前端说没过期、后端说过期」。
   *   `ListStudentCardsController` 已经返 `is_available` ⇒ 直接用它, 不自己重算。
   *
   *   ⚠️ **判定顺序不变** (status → is_activated → 过期), 三句文案一个字没改。
   *   ⚠️ `is_available === false` 的原因**不止**过期, 还有「未激活」——
   *      但未激活那一档在上面 (`is_activated === false`) 已经先判过了,
   *      走到这行时 `is_activated` 必为 true ⇒ `isAvailable()` 里
   *      「!is_activated」那个分支不可能命中 ⇒ 走到这行只可能是
   *      「expires_at 非空且已过期」⇒ 判成「（已过期）」是对的。
   *      (服务端 isAvailable() 的实现: `if (!$this->is_activated) return false;
   *       if ($this->expires_at === null) return true; return !$this->expires_at->isPast();`)
   *   ⚠️ 用 `=== false` 严格比: 字段缺失 (老缓存响应) 时不当成过期。
   *
   * @returns {string} '' = 可用; 否则是给用户看的括号说明
   */
  cardWhy(card) {
    if (!card) return '';
    if (card.status !== 'active') return '（卡已停用）';
    if (card.is_activated === false) return '（未激活）';
    if (card.is_available === false) return '（已过期）';
    return '';
  }

  /** 学员卡选项文案: 名字 · 剩 N (+ 不可用原因) —— 与改前 _selectField 的 label 逐字同款 */
  cardLabel(card) {
    const why = this.cardWhy(card);
    const name = card.name || '卡#' + card.id;
    return `${name} · 剩 ${card.remaining_lessons}` + (why ? ' ' + why : '');
  }

  // ─── 渲染 ───────────────────────────────────────────────────────────
  /** 分区卡片外壳 (照 LessonEditorModal.renderSection())
   *  ⚠️ v0.19.20 (批 41 · 段②) — 实现**搬**进共用入口 `utils/FormPrimitives.js`
   *    (改前 `renderSection` 在本文件里抄了一份 ⇒ 本仓铁律「一个逻辑一个入口」)
   *    ⇒ 本文件的 DOM 结构与样式**一字不动**。 */

  content() {
    // ── v0.18.77 (D3) — 排期预测 (只读) ───────────────────────────────
    //   ⚠️ 数据存在 `this.forecast` (⛔ 不是 this.view —— 会遮蔽 Component.view())。
    //   「共 N 次」= forecast.count, **后端下发** (⛔ 前端不许写死)。
    const forecast = this.forecast;
    const conflict = forecast && forecast.conflict ? forecast.conflict : null;
    const dow = this.currentDow();
    return m("div", {
      className: "ZS-fx-root"
    }, m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "ZS-fx-hint"
    }, "\u6BCF\u5468\u91CD\u590D \xB7 \u6BCF\u6B21\u6263 1 \u8BFE\u65F6 \xB7 \u5B66\u5458\u5361\u5FC5\u987B\u7ED1\u5B9A"), m("div", {
      className: "ZS-modal-form ZS-followup-form ZS-fx-form"
    }, renderSection(1, '排课信息', '选教室 · 教练 · 时间段', [m("div", {
      className: "ZS-form-row ZS-form-row-2col"
    }, m("div", {
      className: "ZS-form-field ZS-fx-room"
    }, m("label", {
      className: "ZS-form-label"
    }, "\u6559\u5BA4", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), this.roomDropdown()), m("div", {
      className: "ZS-form-field ZS-fx-coach"
    }, m("label", {
      className: "ZS-form-label"
    }, "\u6559\u7EC3", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), this.picker.render('fx_coach_id'))), m("div", {
      className: "ZS-form-row full"
    }, m("div", {
      className: "ZS-form-field ZS-fx-time"
    }, m("label", {
      className: "ZS-form-label"
    }, "\u65F6\u95F4", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), this.timeSlotDropdown())),
    // v0.18.77 (D3) — ① 区末尾: 只读的「首次上课日」信息条 + 冲突提示条。
    //   ⛔ **只读提示** —— 不改后端「允许重复创建」的行为; 禁用按钮只是第一道防线。
    //   ⚠️ 这里用 `//` 而不是 `{/* */}`: 数组字面量里 `{/* */}` 会被当成对象字面量
    //   ⇒ babel 直接报 "Unexpected token, expected ','" (实测踩过)。
    forecast && m("div", {
      className: "ZS-fx-first"
    }, m("span", {
      className: "fx-first-label"
    }, m("i", {
      className: "fas fa-calendar-days"
    }), "\u9996\u6B21\u4E0A\u8BFE\u65E5"), m("span", {
      className: "fx-first-date"
    }, forecast.first_date, "\uFF08", DOW_LABEL[forecast.dow], "\uFF09"), m("span", {
      className: "fx-first-meta"
    }, "\u6BCF\u5468\u91CD\u590D \xB7 \u5171 ", m("b", null, forecast.count), " \u6B21\uFF08\u81F3 ", m("b", null, this.lastLabel(forecast.last_date)), "\uFF09")), conflict && m("div", {
      className: "ZS-fx-conflict"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), m("div", null, m("b", null, "\u8BE5\u6559\u5BA4\u8BE5\u65F6\u95F4\u6BB5\u5DF2\u6709\u56FA\u5B9A\u8BFE"), "\uFF1A", conflict.room_name, " \xB7 ", DOW_LABEL[dow] || '', " ", conflict.start_time, "\u2013", conflict.end_time, conflict.student_name ? ' · ' + conflict.student_name : '', conflict.card_name ? ' · ' + conflict.card_name + '（剩 ' + conflict.remaining_lessons + ' 课时）' : '', m("span", {
      className: "fx-conflict-detail"
    }, "\u540C\u4E00\u6559\u5BA4\u540C\u4E00\u65F6\u95F4\u6BB5\u53EA\u80FD\u6709\u4E00\u8282\u8BFE\u3002\u8BF7\u66F4\u6362\u6559\u5BA4\u6216\u65F6\u95F4\u6BB5\u540E\u91CD\u8BD5\u3002")))]), renderSection(2, '学员与卡', '选学员及其持卡', [m("div", {
      className: "ZS-form-row ZS-form-row-2col"
    }, m("div", {
      className: "ZS-form-field ZS-fx-student"
    }, m("label", {
      className: "ZS-form-label"
    }, "\u5B66\u5458", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), this.picker.render('fx_student_id')), m("div", {
      className: "ZS-form-field ZS-fx-card"
    }, m("label", {
      className: "ZS-form-label"
    }, "\u5B66\u5458\u5361", m("span", {
      style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
    }, "*")), this.studentCardDropdown()))]), renderSection(3, '备注', '可留空', [m("div", {
      className: "ZS-form-row full"
    }, m("div", {
      className: "ZS-form-field ZS-fx-notes"
    }, m("label", {
      className: "ZS-form-label"
    }, "\u5907\u6CE8"), m("textarea", {
      className: "ZS-form-input ZS-form-textarea",
      placeholder: "\u5B66\u5458\u7279\u6B8A\u60C5\u51B5 / \u6559\u7EC3\u5907\u6CE8 / \u88C5\u5907\u9700\u6C42...",
      value: this.notes,
      oninput: e => {
        this.notes = e.target.value;
      }
    })))]), this.errorMsg && m("div", {
      className: "ZS-newversion-error",
      style: "margin-top:8px;"
    }, m("i", {
      className: "fas fa-triangle-exclamation",
      style: "color:var(--zs-danger-ef4444, #ef4444);margin-right:6px;"
    }), this.errorMsg))), m("div", {
      className: "Modal-footer"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, "\u53D6\u6D88"), m("button", {
      className: "Button Button--primary",
      disabled: this.saving || !!(this.forecast && this.forecast.conflict),
      onclick: () => this.submit()
    }, this.saving ? '创建中...' : '创建固定课')));
  }

  // ─── 提交 ───────────────────────────────────────────────────────────
  async submit() {
    if (this.saving) return;
    this.errorMsg = '';

    // ⚠️ 星期校验: v0.17.3 删掉弹窗里的星期多选 UI 之后, days 只可能来自调用方。
    //   但 [undefined] 的 size 也是 1, 上面的 !size 拦不住, 放行的话
    //   Array.from(this.days) 会把 [undefined] 发给后端 (Laravel 会 500)。显式拦一次。
    const dayList = Array.from(this.days);
    if (!dayList.length) {
      this.errorMsg = '请至少选择一个星期';
      m.redraw();
      return;
    }
    if (dayList.some(d => d === undefined || d === null || d === '')) {
      this.errorMsg = '未确定星期几, 请取消后从课表上重新打开「添加固定课」';
      m.redraw();
      return;
    }
    if (!dayList.every(d => Number.isInteger(d) && d >= 0 && d <= 6)) {
      this.errorMsg = '星期取值不合法, 请取消后从课表上重新打开「添加固定课」';
      m.redraw();
      return;
    }
    if (!this.coach) {
      this.errorMsg = '请选择教练';
      m.redraw();
      return;
    }
    if (!this.student) {
      this.errorMsg = '请选择学员';
      m.redraw();
      return;
    }
    if (!this.studentCard) {
      this.errorMsg = '固定课必须绑定学员卡 (决策 B6)';
      m.redraw();
      return;
    }
    if (!this.room) {
      this.errorMsg = '请选择教室';
      m.redraw();
      return;
    }
    if (!this.startTime || !this.endTime) {
      this.errorMsg = '请选择时间段';
      m.redraw();
      return;
    }
    this.saving = true;
    m.redraw();
    try {
      await Api.createFixedLessons({
        school_id: this.schoolId,
        room_id: this.room.id,
        coach_id: this.coach.id || this.coach.user_id,
        student_id: this.student.user_id || this.student.id,
        student_card_id: this.studentCard.id,
        start_time: this.startTime,
        end_time: this.endTime,
        // v0.18.40 — 「用户选了哪一档」显式带给后端。
        //   不带的话后端只能拿 start_at 反推归属 (nodeIn 按 sort_order 取第一条)
        //   ⇒ 重叠课表上**排错行** (用户选的那一行永远空着, 前一档被占死)。
        //   Api.createFixedLessons 把 attrs 原样带给后端 ⇒ 这里加了就够, API 层不用改。
        schedule_node_id: this.scheduleNodeId != null ? +this.scheduleNodeId : null,
        // v0.18.41 — 把「用户眼前那一天」(日历那天) 一起带上 ⇒ 后端按**同一份日期**解析
        //   课表版本 (读写同版)。不带的话后端只能按「下一个符合 day_of_week 的日子」
        //   解析版本 —— 课表按 effective_from/effective_to 分了段时, 那两天可能落在
        //   不同的版 ⇒ 后端拿另一个版本的节点清单去核用户选的那个 id ⇒ 422 拒掉用户。
        //   ⚠️ 这是**可选**的: 不带时后端退回 v0.18.40 的老口径, 行为不变。
        schedule_date: this.dateStr || null,
        notes: this.notes || null
      }, dayList);
      this.hide();
      if (this.attrs && this.attrs.onDone) this.attrs.onDone();
    } catch (e) {
      this.saving = false;
      const msg = errReason(e, '创建失败');
      this.errorMsg = '创建失败: ' + msg;
      m.redraw();
    }
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/FixedLessonModal', FixedLessonModal);